import { z } from 'zod';
import { apiGet, apiPost, apiSend } from '@/lib/api';
import { CHANGE_STATUSES } from '@/lib/changes-api';

/**
 * TYP KOLUMNY SPS — LUSTRO `SpsColumnType` z `api/src/config-down/sps-columns.ts`.
 *
 * ⚠️ TA LISTA MUSI ZGADZAĆ SIĘ Z API CO DO NAZWY RODZAJU, bo cały katalog przechodzi przez jeden
 * `parse`. Jeden nieznany `kind` w jednej kolumnie jednej tabeli wywraca CAŁĄ odpowiedź — a ekran
 * pokazuje wtedy pustą listę tabel, nie błąd. Tak było między 2026-07-31 a 2026-08-25: API
 * przemianowało `string` na `text` (commit „Klucze naturalne SPS"), potem doszły `float`
 * i `wallclock` — i przez 25 dni edytor nie otwierał ANI JEDNEJ tabeli, mówiąc przy tym
 * „0 tabel bez mapowania, 0 zakazane", choć obie te liczby są w kodzie stałe.
 *
 * `decimal.pattern` przychodzi jako TEKST — RegExp nie przechodzi przez JSON.
 */
export const ColumnTypeSchema = z.union([
  z.object({ kind: z.literal('int'), min: z.number(), max: z.number() }),
  z.object({ kind: z.literal('text'), minLen: z.number(), maxLen: z.number() }),
  z.object({ kind: z.literal('decimal'), pattern: z.string() }),
  z.object({ kind: z.literal('float'), min: z.number(), max: z.number() }),
  z.object({ kind: z.literal('enum'), values: z.array(z.number()) }),
  /** Data ŚCIENNA — bez strefy. Rodzaj bez pól, cała reguła jest w formacie wartości. */
  z.object({ kind: z.literal('wallclock') }),
]);

export type ColumnType = z.infer<typeof ColumnTypeSchema>;

const ColumnSchema = z.object({
  name: z.string(),
  label: z.string(),
  type: ColumnTypeSchema.nullable(),
  editable: z.boolean(),
});

export type AuthoringColumn = z.infer<typeof ColumnSchema>;

const OwnerSchema = z.union([
  z.object({ kind: z.literal('module'), label: z.string(), route: z.string() }),
  z.object({ kind: z.literal('none') }),
]);

const TableSchema = z.object({
  name: z.string(),
  label: z.string(),
  /** `D` od 29.09.2026 — wyłącznie w tabelach-liściach (`api/src/config-down/authoring-policy.ts`). */
  actions: z.array(z.enum(['I', 'P', 'D'])),
  mappingStatus: z.enum(['confirmed', 'assumed']),
  mappingNote: z.string().nullable(),
  owner: OwnerSchema,
  hasGenerator: z.boolean(),
  key: z.array(ColumnSchema),
  columns: z.array(ColumnSchema),
});

export type AuthoringTable = z.infer<typeof TableSchema>;

/** Wyeksportowany dla testu kontraktu — patrz `authoring-katalog.spec.ts`. */
export const CatalogSchema = z.object({
  tables: z.array(TableSchema),
  unavailable: z.array(z.object({ table: z.string(), reason: z.string() })),
  forbidden: z.array(z.object({ table: z.string(), reason: z.string() })),
  actionsNotOffered: z.array(z.object({ action: z.string(), reason: z.string() })),
});

export type Catalog = z.infer<typeof CatalogSchema>;

export const fetchCatalog = (groupId: string) => apiGet(`/authoring/catalog?groupId=${groupId}`, CatalogSchema);

export const fetchNextKey = (groupId: string, tableName: string) =>
  apiGet(
    `/authoring/next-key?groupId=${groupId}&tableName=${encodeURIComponent(tableName)}`,
    z.object({ tableName: z.string(), field: z.string(), max: z.number(), next: z.number() }),
  );

const CurrentSchema = z.object({
  current: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).nullable(),
});

export const fetchCurrent = (groupId: string, tableName: string, pk: Record<string, string | number>) =>
  apiGet(
    `/authoring/current?groupId=${groupId}&tableName=${encodeURIComponent(tableName)}&pk=${encodeURIComponent(JSON.stringify(pk))}`,
    CurrentSchema,
  );

/**
 * Najwięcej zmian w jednej operacji edytora — LUSTRO `MAX_CHANGES` z `api/src/authoring/authoring.dto.ts`.
 * Pilnuje tego `api/src/authoring/limit-zmian-klienta.spec.ts`: rozjazd dałby przycisk wysyłki,
 * który serwer i tak odrzuci.
 */
export const MAX_ZMIAN_W_OPERACJI = 50;

/** Wartość kolumny w szkicu: zawsze skalar. Obiekt/tablica omijały kiedyś whitelistę kolumn. */
export type CellValue = string | number | boolean | null;

export type DraftChange = {
  tableName: string;
  action: 'I' | 'P' | 'D';
  pk: Record<string, string | number>;
  payload?: Record<string, CellValue>;
};

const WarningSchema = z.object({ code: z.string(), message: z.string(), requiresAck: z.boolean() });

const VerdictSchema = z.object({
  index: z.number(),
  tableName: z.string(),
  action: z.enum(['I', 'P', 'D']),
  pk: z.record(z.string(), z.union([z.string(), z.number()])),
  payload: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
  ok: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(WarningSchema),
  current: z.record(z.string(), z.union([z.string(), z.number(), z.null()])).nullable(),
});

export type Verdict = z.infer<typeof VerdictSchema>;
export type Warning = z.infer<typeof WarningSchema>;

const PreviewSchema = z.object({
  token: z.string(),
  verdicts: z.array(VerdictSchema),
  blocked: z.number(),
  requiresAck: z.array(z.string()),
  currentIsPortalOnly: z.boolean(),
});

export type Preview = z.infer<typeof PreviewSchema>;

export const previewChanges = (groupId: string, changes: DraftChange[]) =>
  apiSend('POST', '/authoring/preview', { groupId, changes }, PreviewSchema);

/**
 * COFNIĘCIE OPERACJI (15.10.2026) — zmiany odwrotne składa serwer ze stanu sprzed operacji.
 * `token` jest `null`, gdy cofnąć się nie da (powody w `blokady`).
 */
const PodgladCofnieciaSchema = z.object({
  mozna: z.boolean(),
  blokady: z.array(z.string()),
  tytul: z.string(),
  zmiany: z.array(
    z.object({
      tableName: z.string(),
      action: z.enum(['I', 'P', 'D']),
      pk: z.record(z.string(), z.union([z.string(), z.number()])),
      payload: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])).optional(),
      zaSeq: z.string(),
      uwagi: z.array(z.string()),
    }),
  ),
  verdicts: z.array(VerdictSchema),
  requiresAck: z.array(z.string()),
  token: z.string().nullable(),
});
export type PodgladCofniecia = z.infer<typeof PodgladCofnieciaSchema>;

export const podgladCofniecia = (groupId: string, opId: string) =>
  apiSend('POST', '/authoring/cofnij/podglad', { groupId, opId }, PodgladCofnieciaSchema);

export const cofnijOperacje = (payload: { groupId: string; opId: string; authorNote: string; previewToken: string; acknowledged: string[] }) =>
  apiSend('POST', '/authoring/cofnij', payload, z.object({ opId: z.string() }));

export const queueChanges = (payload: {
  groupId: string;
  changes: DraftChange[];
  authorNote: string;
  previewToken: string;
  acknowledged: string[];
}) => apiSend('POST', '/authoring/queue', payload, z.object({ opId: z.string() }));

/**
 * ZMIANA HURTOWA — najwięcej zmian naraz, LUSTRO `MAX_ZMIAN_HURTEM` z `api/src/authoring/authoring.dto.ts`
 * (pilnuje `limit-zmian-klienta.spec.ts`). Tyle, ile wierszy ma wynik zapytania.
 */
export const MAX_ZMIAN_HURTEM = 1000;

/**
 * ⚠️ KSZTAŁT JAK W SERWISIE, NIE OSTRZEJ (`podgladHurtu` w `authoring.service.ts`): schemat ciaśniejszy
 * od odpowiedzi pokazałby udany podgląd jako błąd.
 */
const PodgladHurtuSchema = z.object({
  token: z.string(),
  zmian: z.number(),
  czesci: z.array(z.object({ nr: z.number(), akcja: z.enum(['I', 'P', 'D']), zmian: z.number() })),
  blocked: z.number(),
  zablokowane: z.array(
    z.object({
      index: z.number(),
      pk: z.record(z.string(), z.union([z.string(), z.number()])),
      errors: z.array(z.string()),
    }),
  ),
  ostrzezenia: z.array(
    z.object({ code: z.string(), message: z.string(), requiresAck: z.boolean(), zmian: z.number(), pierwsza: z.number() }),
  ),
  requiresAck: z.array(z.string()),
  currentIsPortalOnly: z.boolean(),
});

export type PodgladHurtu = z.infer<typeof PodgladHurtuSchema>;

export const podgladHurtu = (groupId: string, changes: DraftChange[]) =>
  apiSend('POST', '/authoring/hurt/podglad', { groupId, changes }, PodgladHurtuSchema);

/**
 * `blad` ≠ `null` — część operacji już weszła do kolejki, a następna nie. `opIds` to te, które weszły:
 * pójdą na wyspę i ekran ma je śledzić.
 */
export const zakolejkujHurt = (payload: {
  groupId: string;
  changes: DraftChange[];
  authorNote: string;
  previewToken: string;
  acknowledged: string[];
}) =>
  apiSend(
    'POST',
    '/authoring/hurt',
    payload,
    z.object({ opIds: z.array(z.string()), operacji: z.number(), blad: z.string().nullable() }),
  );

const OperationSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Prawdziwy stan operacji — ten sam zestaw co w „Zmianach", bo to ta sama encja. */
  status: z.enum(CHANGE_STATUSES),
  /** Powód niepowodzenia; `null` przy każdym innym stanie. */
  error: z.string().nullable(),
  source: z.string(),
  authorNote: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.object({ email: z.string(), name: z.string().nullable() }).nullable(),
  changes: z.array(
    z.object({
      seq: z.string(),
      tableName: z.string(),
      action: z.string(),
      pk: z.unknown(),
      payload: z.unknown(),
    }),
  ),
});

export type AuthoringOperation = z.infer<typeof OperationSchema>;

const OperationsSchema = z.object({
  rows: z.array(OperationSchema),
  total: z.number(),
  page: z.number(),
  pageSize: z.number(),
});

export const fetchOperations = (q: {
  groupId: string;
  search?: string;
  page: number;
  pageSize: number;
  sort: string;
  dir: 'asc' | 'desc';
}) => {
  const p = new URLSearchParams({
    groupId: q.groupId,
    page: String(q.page),
    pageSize: String(q.pageSize),
    sort: q.sort,
    dir: q.dir,
  });
  if (q.search) p.set('search', q.search);
  return apiGet(`/authoring/operations?${p.toString()}`, OperationsSchema);
};

/**
 * Walidacja wartości po stronie klienta — LUSTRO `sprawdzWartosc` z API, żeby nie ganiać po serwer.
 *
 * ⚠️ KAŻDY RODZAJ Z `ColumnTypeSchema` MUSI MIEĆ TU GAŁĄŹ. Wcześniej wszystko, co nie było
 * `int`/`enum`/`decimal`, wpadało do gałęzi tekstowej i sięgało po `minLen`/`maxLen` — przy
 * `float` i `wallclock` te pola nie istnieją, więc porównania z `undefined` dawały `false`
 * i wartość przechodziła BEZ ŻADNEJ walidacji, żeby dopiero serwer ją odrzucił.
 */
export function validateCell(type: ColumnType | null, raw: string): { value: CellValue; error: string | null } {
  if (!type) return { value: null, error: 'Kolumna bez zadeklarowanego typu jest nieedytowalna' };
  const trimmed = raw.trim();

  if (type.kind === 'int' || type.kind === 'enum') {
    if (trimmed === '') return { value: null, error: 'Podaj liczbę' };
    const n = Number(trimmed);
    if (!Number.isInteger(n)) return { value: null, error: 'Oczekiwano liczby całkowitej' };
    if (type.kind === 'enum') {
      return type.values.includes(n)
        ? { value: n, error: null }
        : { value: n, error: `Dozwolone: ${type.values.join(', ')}` };
    }
    if (n < type.min || n > type.max) return { value: n, error: `Zakres ${type.min}–${type.max}` };
    return { value: n, error: null };
  }

  /** SPS trzyma to jako float i API oczekuje LICZBY — inaczej niż przy kwocie, która jedzie tekstem. */
  if (type.kind === 'float') {
    if (trimmed === '') return { value: null, error: 'Podaj liczbę' };
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return { value: null, error: 'Oczekiwano liczby' };
    if (n < type.min || n > type.max) return { value: n, error: `Zakres ${type.min}–${type.max}` };
    return { value: n, error: null };
  }

  if (type.kind === 'decimal') {
    // kwota jako TEKST — float w JSON gubi grosze
    return new RegExp(type.pattern).test(trimmed)
      ? { value: trimmed, error: null }
      : { value: trimmed, error: 'Kwota, najwyżej dwie cyfry po kropce (np. 19.90)' };
  }

  /**
   * ⚠️ DATA ŚCIENNA — wartość bez strefy, bo to odczyt zegara maszyny klienta, a nie chwila.
   * Doklejone „Z" albo „+02:00" przesuwa granicę ważności konta o godziny, czyli przy dacie
   * granicznej o cały dzień, w którym ktoś nie otworzy automatu. Reguła jak w API.
   */
  if (type.kind === 'wallclock') {
    if (/[zZ]$|[+-]\d{2}:?\d{2}$/.test(trimmed)) {
      return { value: trimmed, error: 'Data ścienna nie ma strefy — usuń „Z" albo przesunięcie z końca' };
    }
    return /^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2})?)?$/.test(trimmed)
      ? { value: trimmed, error: null }
      : { value: trimmed, error: 'Oczekiwano RRRR-MM-DD albo RRRR-MM-DDTHH:MM:SS (bez strefy)' };
  }

  if (trimmed.length < type.minLen) {
    return { value: trimmed, error: type.minLen === 1 ? 'Wartość nie może być pusta' : `Co najmniej ${type.minLen} znaków` };
  }
  if (trimmed.length > type.maxLen) {
    return { value: trimmed, error: `Najwyżej ${type.maxLen} znaków (SPS obciąłby dłuższą wartość bez ostrzeżenia)` };
  }
  return { value: trimmed, error: null };
}

/**
 * KOREKTA MPK W HISTORII WYDAŃ — podgląd i zatwierdzenie (28.09.2026).
 *
 * Kształt odpowiedzi: `api/src/authoring/korekta-mpk.service.ts`. Pola opcjonalne tylko tam, gdzie
 * serwer naprawdę może je pominąć — zod ostrzejszy niż serwis pokazałby udany podgląd jako błąd.
 */
export const PodgladKorektyMpkSchema = z.object({
  pracownik: z.object({ name: z.string(), spsUserNumber: z.string(), mpkBiezace: z.string().nullable() }),
  doZmiany: z.number(),
  juzPoprawne: z.number(),
  wgStarego: z.array(z.object({ mpk: z.string().nullable(), wydan: z.number() })),
  operacji: z.number(),
  wymaganyAgent: z.string(),
  agenciDoAktualizacji: z.array(z.object({ nodeId: z.string(), version: z.string().nullable() })),
  wydania: z.array(
    z.object({
      ts: z.string(),
      transCode: z.string(),
      qty: z.number(),
      automat: z.string(),
      vmid: z.number(),
      towar: z.object({ itemNumber: z.string(), name: z.string() }).nullable(),
      mpk: z.string().nullable(),
    }),
  ),
});
export type PodgladKorektyMpk = z.infer<typeof PodgladKorektyMpkSchema>;

export type ZadanieKorektyMpk = {
  groupId: string;
  employeeId: string;
  /** Czas ścienny automatu, `RRRR-MM-DD GG:MM` — jak przy korekcie ceny. */
  od: string;
  do: string;
  mpk: string;
};

export const podgladKorektyMpk = (z_: ZadanieKorektyMpk) =>
  apiPost('/authoring/korekta-mpk/podglad', z_, PodgladKorektyMpkSchema);

export const wykonajKorekteMpk = (z_: ZadanieKorektyMpk) =>
  apiPost(
    '/authoring/korekta-mpk',
    z_,
    z.object({ opIds: z.array(z.string()), poprawionych: z.number(), pominietych: z.number(), operacji: z.number() }),
  );
